import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { useCallback } from 'react'

export function useInterceptorServiceWithAccessToken(
  service: AxiosInstance,
  accessTokenProvider: () => Promise<string | undefined>,
  accessToken?: string,
) {
  const onBeforeServiceRequest = useCallback(
    async (config: InternalAxiosRequestConfig<any>) => {
      if (!accessToken) accessToken = await accessTokenProvider()

      const bearerToken = `Bearer ${accessToken}`

      config.headers.set('Authorization', bearerToken)

      return config
    },
    [accessTokenProvider, accessToken],
  )

  const onAfterServiceRejectRequest = useCallback(
    async (err) => {
      const oldRequest = err.config
      const responseStatusCode = err.response?.status as number
      const possibleStatusCodes = [401, 403]
      const isPossibleStatusCode =
        possibleStatusCodes.includes(responseStatusCode)

      if (isPossibleStatusCode) {
        try {
          const accessToken = await accessTokenProvider()
          const bearerToken = `Bearer ${accessToken}`

          oldRequest.headers = {
            ...oldRequest.headers,
            Authorization: bearerToken,
          }

          return await axios(oldRequest)
        } catch (authorizationError) {
          return Promise.reject(authorizationError)
        }
      }
      return Promise.reject(err)
    },
    [accessTokenProvider],
  )

  const perform = useCallback(() => {
    const request = service.interceptors.request.use(onBeforeServiceRequest)
    const response = service.interceptors.response.use(
      (_) => _,
      onAfterServiceRejectRequest,
    )

    return () => {
      service.interceptors.request.eject(request)
      service.interceptors.response.eject(response)
    }
  }, [onBeforeServiceRequest, onAfterServiceRejectRequest])

  return { perform }
}
